import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {RootState, useAppDispatch} from "../../../../redux/store";
import {
    fetchBudgets,
    BudgetsList,
} from "./budgetsSlice";
import {
    FiltriBudgets,
    setFiltriBudgets,
    resetFiltriBudgets
} from "components/userPreference/userPreferenceSlice";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import BudgetsPageBreadcrumbs from "./BudgetsPageBreadcrumbs";
import {Soggetto, Tecnica, Commerciale, Stagione} from "components/anagrafiche/anagraficheSlice";
import {OutlinedInput, Paper, SelectChangeEvent, styled} from "@mui/material";
import {theme} from "../../../theme";
import BudgetCommercialeCard from "./commerciali/BudgetCommercialeCard";

// ============================================
// COMPONENT
// ============================================
const Item = styled(Paper)(({theme}) => ({
    ...theme.typography.body2,
    padding: theme.spacing(2),
    textAlign: 'center',
    color: (theme.vars ?? theme).palette.text.secondary,
    ...theme.applyStyles('dark', {
        backgroundColor: '#1A2027',


    }),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    alignContent: 'center',
    alignItems: 'center',
    elevation: 6
}));


const BudgetsPage = () => {
        const dispatch = useAppDispatch();
        const navigate = useNavigate();

        // ============================================
        // SELECTORS - Filtri da userPreference!
        // ============================================

        const budgets = useSelector<RootState, BudgetsList>(
            (state) => state.budgetsState.budgetsList
        );

        const filtri = useSelector<RootState, FiltriBudgets>(
            (state) => state.userPreferenceState.filtriBudgets || {}
        );

        // Anagrafiche per filtri
        const soggetti = useSelector<RootState, Soggetto[]>(
            (state) => state.anagraficheState?.soggetti?.results || []
        );
        const mandatari = soggetti.filter(m => m.is_mandatario);
        const brands = soggetti.filter(m => m.is_brand);
        const confezionisti = soggetti.filter(m => m.is_confezionista);

        const stagioni = useSelector<RootState, Stagione[]>(
            (state) => state.anagraficheState?.stagioni?.results || []
        );

        const commerciali = useSelector<RootState, Commerciale[]>(
            (state) => state.anagraficheState?.commerciali?.results || []
        );

        const tecniche = useSelector<RootState, Tecnica[]>(
            (state) => state.anagraficheState?.tecniche?.results || []
        );

        // ============================================
        // STATI LOCALI - Solo per UI temporanea
        // ============================================

        const [searchValue, setSearchValue] = useState("");
        const [refresh, setRefresh] = useState(false);

        // Stati e tecnica (multipli)
        const [statiSelezionati, setStatiSelezionati] = useState<string[]>(
            filtri?.fase_collezione || []
        );
        const [tecnicheSelezionate, setTecnicheSelezionate] = useState<number[]>(
            filtri?.tecnicheSelezionate || []
        );

        // Filtri singoli
        const [mandatarioSelezionato, setMandatarioSelezionato] = useState<number | undefined>(
            filtri?.mandatario
        );
        const [brandSelezionato, setBrandSelezionato] = useState<number | undefined>(
            filtri?.brand
        );
        const [confezionistaSelezionato, setConfezionistaSelezionato] = useState<number | undefined>(
            filtri?.confezionista
        );
        const [stagioneSelezionata, setStagioneSelezionata] = useState<number | undefined>(
            filtri?.stagione
        );
        const [commercialeSelezionato, setCommercialeSelezionato] = useState<number | undefined>(
            filtri?.commerciale
        );

        // Menu anchors
        const [anchorElStatoFilter, setAnchorElStatoFilter] = useState<null | HTMLElement>(null);
        const [anchorElTecnicaFilter, setAnchorElTecnicaFilter] = useState<null | HTMLElement>(null);

        // ============================================
        // EFFECTS
        // ============================================

        // Caricamento iniziale con filtri salvati
        useEffect(() => {
            stagioneSelezionata && dispatch(fetchBudgets({stagione_id: stagioneSelezionata}));
        }, []);

        // Refresh quando richiesto
        useEffect(() => {
            if (refresh) {
                const nuoviFiltri: FiltriBudgets = {
                    ...filtri,
                    search: searchValue || undefined,
                    fase_collezione: statiSelezionati.length > 0 ? statiSelezionati : undefined,
                    tecnicheSelezionate: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
                    mandatario: mandatarioSelezionato,
                    brand: brandSelezionato,
                    confezionista: confezionistaSelezionato,
                    stagione: stagioneSelezionata,
                    commerciale: commercialeSelezionato,
                    page: 1,  // Reset a pagina 1 quando si applicano filtri
                };

                dispatch(setFiltriBudgets(nuoviFiltri));
                stagioneSelezionata && dispatch(fetchBudgets({stagione_id: stagioneSelezionata}));
                setRefresh(false);
            }
        }, [refresh]);

        // ============================================
        // HANDLERS
        // ============================================

        const handleSearchSubmit = () => {
            setRefresh(true);
        };

        const handleResetFiltri = () => {
            // Reset stati locali
            setSearchValue("");
            setStatiSelezionati(['PROT', 'BDG', 'CONF']);
            setTecnicheSelezionate([]);
            setMandatarioSelezionato(undefined);
            setBrandSelezionato(undefined);
            setConfezionistaSelezionato(undefined);
            setStagioneSelezionata(undefined);
            setCommercialeSelezionato(undefined);

            // Reset in userPreference
            dispatch(resetFiltriBudgets());

            // Fetch con filtri default
            const filtriDefault = {
                numero_record: 10,
                page: 1,
                stato: ['PROT', 'BDG', 'CONF']
            };
            dispatch(setFiltriBudgets(filtriDefault));
            stagioneSelezionata && dispatch(fetchBudgets({stagione_id: stagioneSelezionata}));
        };

        const toggleStato = (stato: string) => {
            setStatiSelezionati(prev =>
                prev.includes(stato)
                    ? prev.filter(s => s !== stato)
                    : [...prev, stato]
            );
        };

        const toggleTecnica = (tecnicaId: number) => {
            setTecnicheSelezionate(prev =>
                prev.includes(tecnicaId)
                    ? prev.filter(t => t !== tecnicaId)
                    : [...prev, tecnicaId]
            );
        };

        // ============================================
        // RENDER
        // ============================================

        return (
            <Box sx={{p: 3}}>
                {/* Header */}
                <Grid container spacing={2} sx={{mb: 1}}>
                    <Grid size={{xs: 12, sm: 6}}>
                        <Stack direction="row" spacing={2} sx={{justifyContent: "flex-start", alignItems: "flex-start",}}>
                            <BudgetsPageBreadcrumbs/>
                        </Stack>

                    </Grid>
                </Grid>

                {/* Filtri Card */}
                <Card sx={{marginBottom: theme.spacing(2), padding: theme.spacing(2),}}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{xs: 12, md: 2.3}}>
                            <Autocomplete
                                value={stagioni.find(s => s.id === stagioneSelezionata) || null}
                                options={stagioni}
                                getOptionLabel={(option) => option.descrizione || ""}
                                onChange={(_, newValue) => {
                                    setStagioneSelezionata(newValue?.id)
                                    setRefresh(true);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params}
                                               sx={{backgroundColor: stagioneSelezionata ? theme.palette.action.selected : 'inherit'}}
                                               label="Stagione"/>
                                )}
                            />
                        </Grid>
                    </Grid>
                </Card>


                {/* Loading */}
                <Backdrop
                    open={budgets.errorsStack.status === ErrorStatusTypes.PENDING}
                    sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
                {stagioneSelezionata &&
                    <Card sx={{
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(2),
                        backgroundColor: theme.palette.secondary.light
                    }}>
                        <Grid container spacing={1}>
                            <Grid size={{xs: 3}}>
                                <Typography variant="h6">
                                    Totali
                                </Typography>
                            </Grid>
                            <Grid size={{xs: 3}} sx={{px: 8}}>
                                <Item elevation={6}>
                                    <Typography variant="h6">
                                        budget
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold'}}>
                                        {Intl.NumberFormat("it-IT", {
                                            style: "decimal",
                                            useGrouping: true,
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(budgets.totali.valore_budget_totale) + " €"}
                                    </Typography>
                                </Item>

                            </Grid>
                            <Grid size={{xs: 3}} sx={{px: 8}}>
                                <Item elevation={6}>
                                    <Typography variant="h6">
                                        confermato
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold'}}>
                                        {Intl.NumberFormat("it-IT", {
                                            style: "decimal",
                                            useGrouping: true,
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(budgets.totali.valore_confermato_totale) + " €"}
                                    </Typography>
                                </Item>
                            </Grid>
                            <Grid size={{xs: 3}} sx={{px: 8}}>
                                <Item elevation={6}>
                                    <Typography variant="h6">
                                        prodotto
                                    </Typography>
                                    <Typography variant="h4" sx={{fontWeight: 'bold'}}>
                                        {Intl.NumberFormat("it-IT", {
                                            style: "decimal",
                                            useGrouping: true,
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(budgets.totali.valore_prodotto_totale) + " €"}
                                    </Typography>
                                </Item>
                            </Grid>
                        </Grid>
                    </Card>
                }

                {stagioneSelezionata && budgets.budgets.map((budget) =>
                    <BudgetCommercialeCard
                        key={`${budget.commerciale}__${stagioneSelezionata}`}
                        budget={budget}
                        stagione_id={stagioneSelezionata!}
                    />
                )}


            </Box>
        )
            ;
    }
;

export default BudgetsPage;